import React, { useState, useRef, useEffect, useCallback } from 'react';
import { isInvalidPhotoUrl } from '../utils/photoUtils';
import api, { translateAPI } from '../services/api';

function calculateAge(dob) {
  if (!dob) return '';
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age > 0 ? String(age) : '';
}

const WarningIcon = ({ size = 12, color = '#bbb' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

export const ProfileDisclaimer = ({ style = {}, language }) => {
  const isZh = language === 'zh' || (typeof window !== 'undefined' && (localStorage.getItem('language') || 'zh') === 'zh');
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '12px 0', fontSize: 10.5, letterSpacing: '.04em', color: '#6E685F', ...style }}>
      <WarningIcon size={12} color="#8A6D3B" />
      <span>{isZh
        ? '风险提示：资料均由被推荐人本人提供，本公司不对其真实性作担保。如有需要，可委托合作律师或第三方机构进行背景调查。'
        : 'Risk notice: all details were provided by the referred individual and are not guaranteed by us. Background checks are available through partner attorneys or third-party agencies on request.'}</span>
    </div>
  );
};

// 活动渠道引流的会员专用风险提示. Rendered wherever such a profile is shown to
// someone who might act on it — the 相亲卡 and the client-portal detail sheet.
// `isEventMember` comes from the API as `is_event_member` (a profile with a row
// in event_registrations); the component renders nothing for everyone else.
export const EventMemberRiskNotice = ({ isEventMember, language, style = {} }) => {
  if (!isEventMember) return null;
  const isZh = language === 'zh' || (language !== 'en' && typeof window !== 'undefined'
    && (localStorage.getItem('language') || 'zh') === 'zh');
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: 10.5, lineHeight: 1.4, color: '#bbb', ...style }}>
      <WarningIcon size={12} color="#bbb" />
      <span>{isZh
        ? '风险提示：该会员由活动渠道引流，资料均由本人提供，本公司不对其真实性作担保。如有需要，可委托合作律师或第三方机构进行背景调查。'
        : 'Risk notice: this member came through an event channel. All details were self-reported and are not verified by us. Background checks are available through partner attorneys or third-party agencies on request.'}</span>
    </div>
  );
};

const MatchmakingCard = ({ profile, customFields = [], preferences = [], onClose, language = 'zh' }) => {
  // Default to ZH so the card opens in Chinese on first render (the modal toolbar
  // still lets users switch to EN). Was 'en' before — produced English-only output
  // for /profiles users in China.
  const [cardLang, setCardLang] = useState('zh');
  const isZh = cardLang === 'zh';
  const cardRef = useRef(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Download progress: null when idle, else { pct: 0-100, label }.
  const [progress, setProgress] = useState(null);
  // Safari sometimes ignores a programmatic <a download>.click() that happens
  // after awaits (it is no longer inside the user gesture). The staged file's
  // URL is kept here and offered as a real link the user can click.
  const [manualUrl, setManualUrl] = useState(null);
  const originalBio = useRef(profile.bio || '');
  const translatedBio = useRef('');

  // ── i18n translation maps ──
  // These cover the most common English values stored in profile / custom fields.
  // Values that aren't in the map fall through to the original string.
  const COUNTRY_ZH = {
    'united states': '美国', 'united states of america': '美国', 'usa': '美国', 'us': '美国', 'u.s.': '美国', 'u.s.a.': '美国',
    'canada': '加拿大', 'china': '中国', 'taiwan': '台湾', 'hong kong': '香港', 'macao': '澳门', 'macau': '澳门',
    'singapore': '新加坡', 'malaysia': '马来西亚', 'australia': '澳大利亚', 'new zealand': '新西兰',
    'united kingdom': '英国', 'uk': '英国', 'england': '英国',
    'japan': '日本', 'south korea': '韩国', 'korea': '韩国',
    'germany': '德国', 'france': '法国', 'italy': '意大利', 'spain': '西班牙', 'netherlands': '荷兰',
  };
  const STATE_ZH = {
    'california': '加州', 'ca': '加州',
    'new york': '纽约州', 'ny': '纽约州',
    'texas': '德州', 'tx': '德州',
    'washington': '华盛顿州', 'wa': '华盛顿州',
    'massachusetts': '马萨诸塞州', 'ma': '马萨诸塞州',
    'illinois': '伊利诺伊州', 'il': '伊利诺伊州',
    'florida': '佛罗里达州', 'fl': '佛罗里达州',
    'oregon': '俄勒冈州', 'or': '俄勒冈州',
    'pennsylvania': '宾夕法尼亚州', 'pa': '宾夕法尼亚州',
    'new jersey': '新泽西州', 'nj': '新泽西州',
    'virginia': '弗吉尼亚州', 'va': '弗吉尼亚州',
    'georgia': '佐治亚州', 'ga': '佐治亚州',
    'nevada': '内华达州', 'nv': '内华达州',
    'arizona': '亚利桑那州', 'az': '亚利桑那州',
    'ohio': '俄亥俄州', 'oh': '俄亥俄州',
    'michigan': '密歇根州', 'mi': '密歇根州',
    'maryland': '马里兰州', 'md': '马里兰州',
    'colorado': '科罗拉多州', 'co': '科罗拉多州',
    'minnesota': '明尼苏达州', 'mn': '明尼苏达州',
    'north carolina': '北卡罗来纳州', 'nc': '北卡罗来纳州',
    'hawaii': '夏威夷', 'hi': '夏威夷',
  };
  const trCountry = (c) => c ? (isZh ? (COUNTRY_ZH[String(c).toLowerCase().trim()] || c) : c) : '';
  const trState   = (s) => s ? (isZh ? (STATE_ZH[String(s).toLowerCase().trim()] || s) : s) : '';
  // Translate a custom-field value via its field_options ([{en, zh}]). Handles
  // single string, comma-joined string, and array inputs.
  const translateOption = (rawVal, optionsRaw) => {
    if (!isZh || rawVal == null || rawVal === '') return rawVal;
    let opts = optionsRaw;
    try { opts = typeof opts === 'string' ? JSON.parse(opts) : opts; } catch (e) { opts = null; }
    if (!Array.isArray(opts) || !opts[0] || (!opts[0].en && !opts[0].label)) return rawVal;
    const lookup = (s) => {
      const k = String(s).trim();
      const m = opts.find(o => o.en === k || o.label === k);
      return m ? (m.zh || m.label_zh || k) : k;
    };
    if (Array.isArray(rawVal)) return rawVal.map(lookup).join('、');
    const s = String(rawVal);
    if (s.includes(',')) return s.split(',').map(x => lookup(x.trim())).join('、');
    return lookup(s);
  };
  // Format any range-shaped value as "X ~ Y" (with cm/year suffix when relevant).
  const formatRange = (fieldNameLower, start, end) => {
    let s = start, e = end;
    if (fieldNameLower.includes('age')) { if (!s) s = '18'; if (!e) e = '99'; return `${s} ~ ${e}`; }
    if (fieldNameLower.includes('heigh')) { if (!s) s = '150'; if (!e) e = '200'; return `${s} cm ~ ${e} cm`; }
    if (fieldNameLower.includes('weigh')) { if (!s) s = '40';  if (!e) e = '120'; return `${s} kg ~ ${e} kg`; }
    if (s == null && e == null) return '';
    return `${s ?? ''} ~ ${e ?? ''}`;
  };

  const extractFieldValue = (fn) => {
    const field = customFields.find(f => (f.field_name || '').toLowerCase() === fn.toLowerCase());
    if (!field) return '';
    let val = field.field_value;
    if (val == null || val === '') return '';
    const fnLower = (field.field_name || '').toLowerCase();
    // Parse JSON strings to surface ranges / arrays / 3-dim objects.
    if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
      try { val = JSON.parse(val); } catch (e) { /* keep as string */ }
    }
    if (Array.isArray(val)) return translateOption(val, field.field_options);
    if (typeof val === 'object' && val !== null) {
      // 3-dim preference object → use the acceptable tier (often a nested JSON range).
      if ('ideal' in val || 'acceptable' in val || 'bottom_line' in val) {
        let pick = val.acceptable || val.ideal || val.bottom_line || '';
        if (typeof pick === 'string' && (pick.startsWith('{') || pick.startsWith('['))) {
          try { pick = JSON.parse(pick); } catch (e) {}
        }
        if (Array.isArray(pick)) return translateOption(pick, field.field_options);
        if (pick && typeof pick === 'object') {
          if ('start' in pick || 'end' in pick) return formatRange(fnLower, pick.start, pick.end);
          if ('min' in pick || 'max' in pick) return formatRange(fnLower, pick.min, pick.max);
        }
        return translateOption(String(pick || ''), field.field_options);
      }
      if ('start' in val || 'end' in val) return formatRange(fnLower, val.start, val.end);
      if ('min' in val || 'max' in val) return formatRange(fnLower, val.min, val.max);
      return '';
    }
    return translateOption(val, field.field_options);
  };

  // Fill missing range bounds with defaults for known fields
  const fillRange = (fieldName, start, end) => {
    const fn = (fieldName || '').toLowerCase();
    let s = start, e = end;
    if (fn.includes('age')) { if (!s) s = '18'; if (!e) e = '99'; }
    else if (fn.includes('heigh')) { if (!s) s = '150'; if (!e) e = '200'; s += ' cm'; e += ' cm'; }
    return `${s} ~ ${e}`;
  };

  const compileLookingFor = () => {
    let prefs = preferences.filter(p => p.field_value && p.field_value !== 'null' && p.field_value !== '');
    if (!prefs.length) prefs = customFields.filter(f => (f.field_name || '').startsWith('mate_') && f.field_value && f.field_value !== 'null' && f.field_value !== '');
    // Recursively normalize a value into a display string. Handles nested JSON
    // strings, 3-dim preference objects, range objects, and arrays.
    const normalize = (v, fnLower, fieldOptions) => {
      if (v == null || v === '') return '';
      if (typeof v === 'string') {
        const s = v.trim();
        if ((s.startsWith('{') || s.startsWith('[')) && (s.endsWith('}') || s.endsWith(']'))) {
          try { return normalize(JSON.parse(s), fnLower, fieldOptions); } catch (e) { /* fall through */ }
        }
        return translateOption(s, fieldOptions);
      }
      if (Array.isArray(v)) {
        return translateOption(v, fieldOptions) || v.join(', ');
      }
      if (typeof v === 'object') {
        if ('ideal' in v || 'acceptable' in v || 'bottom_line' in v) {
          // Prefer acceptable tier (mid range / common requirement).
          return normalize(v.acceptable || v.ideal || v.bottom_line || '', fnLower, fieldOptions);
        }
        if ('start' in v || 'end' in v) return formatRange(fnLower, v.start, v.end);
        if ('min' in v || 'max' in v) return formatRange(fnLower, v.min, v.max);
      }
      return '';
    };
    return prefs.map(p => {
      const label = isZh && p.field_label_zh ? p.field_label_zh : (p.field_label || p.field_name || '');
      const fnLower = (p.field_name || '').toLowerCase();
      const val = normalize(p.field_value, fnLower, p.field_options);
      if (!val || val === '{}' || val === '[]' || String(val).includes('[object Object]')) return null;
      return `${label}: ${val}`;
    }).filter(Boolean).join('; ');
  };

  const parseTags = () => {
    const g = (profile.gender || '').toLowerCase();
    let t = g === 'male' ? extractFieldValue('tag1') : g === 'female' ? extractFieldValue('tag2') : '';
    if (!t) t = extractFieldValue('tag1') || extractFieldValue('tag2') || extractFieldValue('personality');
    if (t && typeof t === 'string' && t.startsWith('[')) { try { const a = JSON.parse(t); if (Array.isArray(a)) t = a.join(', '); } catch(e){} }
    return t;
  };

  const parseInterests = () => {
    let v = extractFieldValue('hobbies') || extractFieldValue('interests');
    if (v && typeof v === 'string' && v.startsWith('[')) { try { const a = JSON.parse(v); if (Array.isArray(a)) v = a.join(', '); } catch(e){} }
    return v;
  };

  // Build location string with state / country translated into Chinese when isZh.
  const buildLocation = () => {
    const city = profile.location_city || '';
    const state = trState(profile.location_state || '');
    const country = trCountry(profile.location_country || '');
    return [city, state, country].filter(Boolean).join(isZh ? '、' : ', ');
  };

  const [cardData, setCardData] = useState(() => ({
    // Show the WeChat name (weixin) on the card; fall back to the first name only
    // (never the surname) when there's no WeChat name on file.
    name: (extractFieldValue('weixin') || profile.first_name || '').trim(),
    offlineId: extractFieldValue('offlineid') || profile.offline_id || profile.id || '',
    location: buildLocation(),
    about: profile.bio || '',
    age: calculateAge(profile.date_of_birth) || '',
    height: extractFieldValue('height') || extractFieldValue('身高'),
    maritalStatus: extractFieldValue('marital_status') || extractFieldValue('love') || extractFieldValue('婚姻状况'),
    childStatus: extractFieldValue('child') || extractFieldValue('children') || extractFieldValue('小孩情况'),
    education: profile.education || extractFieldValue('education') || extractFieldValue('edu'),
    occupation: profile.occupation || '',
    race: extractFieldValue('race') || extractFieldValue('nation') || extractFieldValue('民族'),
    income: extractFieldValue('pay') || extractFieldValue('income') || extractFieldValue('月收入'),
    realEstate: extractFieldValue('real_estate') || extractFieldValue('房产'),
    personality: parseTags(),
    interests: parseInterests(),
    lookingFor: compileLookingFor(),
  }));

  // Re-translate values whenever the user toggles EN/中文 on the card toolbar.
  useEffect(() => {
    setCardData(prev => ({
      ...prev,
      location: buildLocation(),
      height: extractFieldValue('height') || extractFieldValue('身高'),
      maritalStatus: extractFieldValue('marital_status') || extractFieldValue('love') || extractFieldValue('婚姻状况'),
      childStatus: extractFieldValue('child') || extractFieldValue('children') || extractFieldValue('小孩情况'),
      education: profile.education || extractFieldValue('education') || extractFieldValue('edu'),
      race: extractFieldValue('race') || extractFieldValue('nation') || extractFieldValue('民族'),
      income: extractFieldValue('pay') || extractFieldValue('income') || extractFieldValue('月收入'),
      realEstate: extractFieldValue('real_estate') || extractFieldValue('房产'),
      personality: parseTags(),
      interests: parseInterests(),
      lookingFor: compileLookingFor(),
      about: originalBio.current || prev.about,
    }));
  }, [cardLang, profile]); // re-translate when toggling EN↔ZH or when the profile changes

  // When EN is selected, translate the 关于我 / About text (free-form bio) to
  // English via the translate endpoint; fall back to the original on any error
  // or when the server has no translation. Restores the original on 中文.
  useEffect(() => {
    let alive = true;
    const src = originalBio.current || '';
    if (cardLang === 'en' && src.trim()) {
      translateAPI.translate(src, 'zh', 'en')
        .then(res => {
          const tr = res?.data?.translation;
          if (alive && tr && tr.trim() && tr !== src) {
            setCardData(prev => ({ ...prev, about: tr }));
          }
        })
        .catch(() => { /* keep original */ });
    } else if (cardLang === 'zh') {
      setCardData(prev => ({ ...prev, about: src || prev.about }));
    }
    return () => { alive = false; };
  }, [cardLang]);

  // Photo helpers
  const getPhotoUrl = (url) => {
    if (isInvalidPhotoUrl(url)) return null;
    const s = String(url).trim();
    if (s.length < 4) return null;
    if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:')) {
      return s.includes('_s.') ? s.replace('_s.', '_b.') : s;
    }
    // New-system photos stored under /uploads/ → serve from current origin
    if (s.startsWith('/uploads/') || s.startsWith('/up/')) {
      return s;
    }
    return 'https://match2.findu.love/up/' + (s.includes('_s.') ? s.replace('_s.', '_b.') : s);
  };

  let rawGallery = profile.gallery || profile.gallery_photos || [];
  if (typeof rawGallery === 'string') { try { rawGallery = JSON.parse(rawGallery); } catch(e) { rawGallery = []; } }
  if (!Array.isArray(rawGallery)) rawGallery = [];
  const galleryPhotos = rawGallery.map(p => typeof p === 'object' && p ? (p.url||p.src||p.path||'') : String(p||''))
    .filter(p => p && p.length > 3 && !isInvalidPhotoUrl(p)).map(p => getPhotoUrl(p)).filter(Boolean);
  const mainPhoto = getPhotoUrl(profile.profile_photo || profile.photo_url || '');
  const photoList = [...new Set([mainPhoto, ...galleryPhotos].filter(Boolean))].slice(0, 2);

  // Safari never finishes html2canvas on this card — it clones the document
  // into an iframe and waits on that clone's fonts/stylesheets, and that wait
  // does not come back (the 50% → "render timeout" report).
  //
  // This path avoids html2canvas entirely: the card is styled 100% inline and
  // every image has already been swapped to a data URL, so the element can be
  // serialised straight into an <svg><foreignObject> and decoded as an image.
  // No document clone, no font wait, no network — and the image load is
  // time-boxed, so the worst case is a clean failure instead of a frozen bar.
  const renderViaSvg = async (el, scale, timeoutMs) => {
    const w = Math.ceil(el.offsetWidth || 794);
    const h = Math.ceil(el.scrollHeight || el.offsetHeight || 480);
    const base = el.getBoundingClientRect();

    // An SVG loaded as an <img> runs in "secure static mode": Safari refuses to
    // paint the images inside it, and because the photos are `height:auto`
    // their boxes then collapse and the layout shifts. So the images are
    // measured first, replaced with same-size spacers in the clone (layout
    // preserved), and painted onto the canvas afterwards from the live DOM —
    // where they are already decoded data URLs.
    const liveImgs = [...el.querySelectorAll('img')];
    const boxes = liveImgs.map(im => {
      const r = im.getBoundingClientRect();
      return { x: r.left - base.left, y: r.top - base.top, w: r.width, h: r.height };
    });

    const clone = el.cloneNode(true);
    clone.removeAttribute('id');
    const cloneImgs = [...clone.querySelectorAll('img')];
    cloneImgs.forEach((node, i) => {
      const b = boxes[i] || { w: 0, h: 0 };
      const spacer = document.createElement('span');
      const disp = (getComputedStyle(liveImgs[i]).display || 'inline-block');
      spacer.setAttribute('style', `display:${disp === 'inline' ? 'inline-block' : disp};width:${b.w}px;height:${b.h}px`);
      node.parentNode.replaceChild(spacer, node);
    });
    // Inputs/textareas do not paint their value inside foreignObject; the card
    // is captured in preview mode, but be safe and swap any that remain.
    clone.querySelectorAll('input, textarea').forEach(node => {
      const span = document.createElement('span');
      span.textContent = node.value || '';
      span.setAttribute('style', node.getAttribute('style') || '');
      node.parentNode.replaceChild(span, node);
    });
    // A generic stack: webfonts cannot load inside a data-URL SVG, and the
    // html2canvas path drops them too, so both renderers match.
    clone.style.fontFamily = "-apple-system, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif";
    clone.style.width = `${w}px`;
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';

    const xml = new XMLSerializer().serializeToString(clone);
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">` +
      `<foreignObject x="0" y="0" width="${w}" height="${h}">` +
      `<div xmlns="http://www.w3.org/1999/xhtml" style="width:${w}px;background:#fff">${xml}</div>` +
      `</foreignObject></svg>`;
    const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);

    const img = new Image();
    await Promise.race([
      new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('svg render failed'));
        img.src = url;
      }),
      new Promise((_, rej) => setTimeout(() => rej(new Error('svg render timeout')), timeoutMs)),
    ]);

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.drawImage(img, 0, 0);

    // Paint the real bitmaps over the spacers, in z-order so the watermark
    // (z-index 5, rotated, 18% opacity) still lands on top of the photos.
    const zOf = (node) => {
      let z = 0, n = node;
      while (n && n !== el.parentElement) {
        const v = parseInt(getComputedStyle(n).zIndex, 10);
        if (!isNaN(v)) z = Math.max(z, v);
        n = n.parentElement;
      }
      return z;
    };
    const opacityOf = (node) => {
      let o = 1, n = node;
      while (n && n !== el.parentElement) {
        const v = parseFloat(getComputedStyle(n).opacity);
        if (!isNaN(v)) o *= v;
        n = n.parentElement;
      }
      return o;
    };
    // Rotation comes from the nearest transformed ancestor (the watermark
    // wrapper). getBoundingClientRect on a rotated element returns the
    // axis-aligned box, so the untransformed size is used with an explicit
    // rotation about the box's centre instead.
    const rotationOf = (node) => {
      let n = node;
      while (n && n !== el.parentElement) {
        const tr = getComputedStyle(n).transform;
        if (tr && tr !== 'none') {
          const m = tr.match(/matrix\(([^)]+)\)/);
          if (m) {
            const [a, b2] = m[1].split(',').map(Number);
            return Math.atan2(b2, a);
          }
        }
        n = n.parentElement;
      }
      return 0;
    };

    const order = liveImgs.map((im, i) => ({ im, i })).sort((p1, p2) => zOf(p1.im) - zOf(p2.im));
    for (const { im, i } of order) {
      const b = boxes[i];
      if (!b || !b.w || !b.h || !im.naturalWidth) continue;
      const radius = parseFloat(getComputedStyle(im).borderRadius) || 0;
      const angle = rotationOf(im);
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, opacityOf(im)));
      if (angle) {
        // draw centred on the box, rotated, at the element's own (unrotated) size
        const cw = im.offsetWidth || b.w;
        const ch = im.offsetHeight || b.h;
        ctx.translate(b.x + b.w / 2, b.y + b.h / 2);
        ctx.rotate(angle);
        ctx.drawImage(im, -cw / 2, -ch / 2, cw, ch);
      } else {
        if (radius > 0 && ctx.roundRect) {
          ctx.beginPath();
          ctx.roundRect(b.x, b.y, b.w, b.h, radius);
          ctx.clip();
        }
        ctx.drawImage(im, b.x, b.y, b.w, b.h);
      }
      ctx.restore();
    }

    // Fail here, not at upload time, if this browser taints the canvas for
    // foreignObject images — the caller then falls back to html2canvas.
    canvas.toDataURL('image/png');
    return canvas;
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setManualUrl(null);
    const setProg = (pct, label) => setProgress({ pct: Math.round(pct), label });
    setProg(3, isZh ? '准备中…' : 'Preparing…');
    // Capture in preview mode so the generated image matches the preview
    // exactly (empty fields hidden, no input chrome).
    setPreviewMode(true);
    await new Promise(r => setTimeout(r, 120));
    const el = document.getElementById('matchmaking-card-content');
    if (!el) { setDownloading(false); setProgress(null); return; }
    const imgs = [...el.querySelectorAll('img')];
    const origSrcs = [];
    const restore = () => {
      el.style.maxHeight = ''; el.style.overflow = '';
      el.querySelectorAll('input, textarea').forEach(e => { e.style.border = '1px dashed #ccc'; e.style.background = ''; });
      // Put the original (http) src back for the on-screen modal. We embed as
      // data URLs during capture (not blob URLs — Safari fails to load blob
      // URLs inside html2canvas → WebKitBlobResource error), so nothing to revoke.
      imgs.forEach((img, i) => { if (origSrcs[i] && img.src !== origSrcs[i]) img.src = origSrcs[i]; });
    };
    const TRANSPARENT_PX = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
    const sameOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
    // Fetch any URL and return a data URL, time-bounded. null on failure.
    const fetchAsDataUrl = async (url, timeoutMs) => {
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), timeoutMs);
      try {
        const resp = await fetch(url, { signal: ctrl.signal });
        clearTimeout(tid);
        if (!resp.ok) return null;
        const blob = await resp.blob();
        return await new Promise((res2, rej2) => {
          const fr = new FileReader();
          fr.onload = () => res2(fr.result);
          fr.onerror = rej2;
          fr.readAsDataURL(blob);
        });
      } catch (e) { clearTimeout(tid); return null; }
    };
    // Safari rasterises slower and caps canvas area lower than Chrome, so it
    // starts at a smaller scale instead of burning a 30s timeout first.
    const isSafari = typeof navigator !== 'undefined'
      && /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent || '');

    try {
      const html2canvas = (await import('html2canvas')).default;
      // Inline EVERY image (photos AND logos) as a data URL BEFORE rendering so
      // html2canvas has ZERO network to do — that's what kept hanging it (a slow
      // proxy / blocked host on the China↔US link → per-image waits → render
      // timeout). With everything pre-inlined, the render is purely local and
      // can't stall on a network request. Failed images become a transparent
      // pixel (blank) rather than blocking. Spans 5% → 45% of the bar.
      imgs.forEach(img => origSrcs.push(img.src || ''));
      // Fetch each UNIQUE remote/same-origin image ONCE and in PARALLEL (the
      // logo repeats, and serial fetches were the main slowdown). Cache by src.
      const uniqueSrcs = [...new Set(imgs.map(im => im.src).filter(s => s && !s.startsWith('data:')))];
      const cache = {};
      let done = 0;
      const total = uniqueSrcs.length || 1;
      await Promise.all(uniqueSrcs.map(async (s) => {
        let dataUrl = null;
        if (sameOrigin && s.startsWith(sameOrigin)) dataUrl = await fetchAsDataUrl(s, 8000);
        else if (/^https?:\/\//i.test(s)) dataUrl = await fetchAsDataUrl(`/api/image-proxy?url=${encodeURIComponent(s)}`, 15000);
        cache[s] = dataUrl || TRANSPARENT_PX;
        done++;
        setProg(5 + (done / total) * 40, isZh ? `处理图片 ${done}/${total}…` : `Processing images ${done}/${total}…`);
      }));
      // Apply cached data URLs to every img (incl. duplicates of the logo).
      imgs.forEach(img => { const s = img.src || ''; if (s && !s.startsWith('data:') && cache[s]) img.src = cache[s]; });
      // Data URLs decode locally — a short settle is enough (no per-img await).
      await new Promise(r => setTimeout(r, 150));

      el.style.maxHeight = 'none';
      el.style.overflow = 'visible';
      el.querySelectorAll('input, textarea').forEach(e => { e.style.border = 'none'; e.style.background = 'transparent'; e.style.outline = 'none'; });

      setProg(50, isZh ? '生成图片…' : 'Rendering image…');
      // useCORS:false + allowTaint:false: every image is now a same-origin data
      // URL, so no cross-origin loading is attempted and the canvas stays
      // exportable. imageTimeout is short purely as a backstop.
      //
      // onclone: html2canvas clones the whole document into an internal iframe
      // and waits for that clone's fonts/stylesheets to load — on Safari that
      // wait can stall forever (the "stuck at 50% → render timeout" symptom).
      // The card is styled 100% inline, so we strip the @font-face / font
      // stylesheets AND remove font-family from text in the CLONE: this avoids
      // the font-load wait entirely. Colours (var(--color-burgundy)) are read
      // from the live element's computed style, so they still render correctly.
      const renderOpts = (scale) => ({
        scale, useCORS: false, allowTaint: false, backgroundColor: '#fff',
        logging: false, width: 794, imageTimeout: 3000,
        onclone: (doc) => {
          try {
            doc.querySelectorAll('link[rel="stylesheet"][href*="font"], style').forEach(n => {
              const txt = (n.textContent || '') + (n.getAttribute && n.getAttribute('href') || '');
              if (/font-face|fonts?\./i.test(txt)) n.remove();
            });
          } catch (e) { /* non-fatal */ }
        },
      });
      const renderOnce = (scale, timeoutMs) => Promise.race([
        html2canvas(el, renderOpts(scale)),
        new Promise((_, rej) => setTimeout(() => rej(new Error('render timeout')), timeoutMs)),
      ]);
      // Primary render at full scale. On a timeout (slow China↔US link + large
      // inlined photos can make the scale-2 rasterization overrun 30s), retry
      // once at a lower scale — smaller canvas, far less work — instead of
      // failing outright. Only the failing path is affected; a successful
      // full-scale render is unchanged.
      let canvas = null;
      if (isSafari) {
        // Preferred on Safari: no document clone, so nothing to stall on.
        try {
          canvas = await renderViaSvg(el, 2, 20000);
        } catch (svgErr) {
          console.warn('[card] svg render failed, falling back to html2canvas:', svgErr.message);
          setProg(55, isZh ? '换一种方式生成…' : 'Trying another renderer…');
        }
      }
      if (!canvas) {
        try {
          canvas = await renderOnce(isSafari ? 1.5 : 2, 30000);
        } catch (rErr) {
          setProg(55, isZh ? '重试生成…' : 'Retrying…');
          canvas = await renderOnce(isSafari ? 1 : 1.4, 25000);
        }
      }

      restore();
      setProg(65, isZh ? '生成图片…' : 'Rendering image…');

      const baseName = `${cardData.name || 'profile'}-card`;

      // Stage the PNG on the server, then download it via a server URL that
      // sends Content-Disposition: attachment (reliable auto-download across
      // browsers incl. Safari). The server deletes the temp file once streamed.
      // Export to a binary Blob (not base64) — ~25% smaller payload + no
      // base64 encode/decode, so the upload over a slow link is faster.
      const dataUrlToBlob = (dataUrl) => {
        const [head, b64] = String(dataUrl).split(',');
        const mime = (head.match(/:(.*?);/) || [])[1] || 'image/png';
        const bin = atob(b64 || '');
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        return new Blob([arr], { type: mime });
      };
      let blob = await Promise.race([
        new Promise((resolve) => {
          try { canvas.toBlob((b) => resolve(b || null), 'image/png'); }
          catch (taintErr) { resolve(null); }
        }),
        // Safari can drop the toBlob callback entirely; don't wait forever.
        new Promise((resolve) => setTimeout(() => resolve(null), 15000)),
      ]);
      if (!blob) {
        setProg(70, isZh ? '换一种方式导出…' : 'Exporting another way…');
        blob = dataUrlToBlob(canvas.toDataURL('image/png'));   // throws only when tainted
      }
      if (!blob || !blob.size) throw new Error('canvas export produced no data');
      // Upload spans 65% → 95% via axios upload-progress.
      const fd = new FormData();
      fd.append('image', blob, `${baseName}.png`);
      const r = await api.post('/matchmaking-card', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,        // axios has NO default timeout — a stalled upload used to hang the bar
        onUploadProgress: (e) => {
          if (e.total) setProg(65 + (e.loaded / e.total) * 30, isZh ? '上传中…' : 'Uploading…');
        },
      });
      const token = r?.data?.token;
      if (!token) throw new Error('No download token');

      setProg(100, isZh ? '开始下载…' : 'Downloading…');
      const apiBase = (api.defaults.baseURL || '/api').replace(/\/$/, '');
      const url = `${apiBase}/matchmaking-card/download/${token}?name=${encodeURIComponent(baseName)}`;
      const link = document.createElement('a');
      link.href = url;
      link.download = `${baseName}.png`;
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { try { link.remove(); } catch (e) { /* */ } }, 4000);
      // The staged file is single-use but stays valid until fetched, so a
      // visible link is a safe backup for browsers that ignore the click.
      setManualUrl(url);
      setTimeout(() => setProgress(null), 1200);
    } catch (err) {
      console.error('Download error:', err);
      try { restore(); } catch (e) { /* */ }
      // Surface the failure in the progress strip (red) instead of silently
      // dropping it, so the user knows to retry rather than staring at a frozen
      // bar that disappears.
      // Say WHAT failed — a generic message left nobody able to report where
      // it stopped, which is exactly what made the Safari case hard to pin down.
      const why = String(err && (err.message || err)) || '';
      setProgress({
        pct: 100,
        label: (isZh ? '生成失败：' : 'Failed: ') + why.slice(0, 60),
        error: true,
      });
      setTimeout(() => setProgress(null), 8000);
    } finally {
      setDownloading(false);
    }
  };

  const hasBasics = cardData.age || cardData.height || cardData.maritalStatus || cardData.childStatus || cardData.education || cardData.occupation || cardData.race || cardData.income || cardData.realEstate;

  // Editable text — in preview mode renders as plain div, in edit mode as input/textarea
  const inputBase = { border: '1px dashed #ccc', borderRadius: 4, padding: '2px 6px', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
  const previewBase = { border: 'none', background: 'transparent', padding: '2px 0' };
  const iStyle = previewMode ? previewBase : inputBase;

  const Field = ({ label, field }) => {
    if (previewMode && !cardData[field]) return null;
    return (
      <div style={{ marginBottom: 3, fontSize: 13 }}>
        <span style={{ fontWeight: 500, color: '#555' }}>{label}: </span>
        {previewMode ? (
          <span style={{ color: '#333' }}>{cardData[field]}</span>
        ) : (
          <input value={cardData[field]} onChange={e => setCardData(p => ({ ...p, [field]: e.target.value }))} style={{ ...iStyle, width: '55%' }} />
        )}
      </div>
    );
  };

  const TextBlock = ({ title, field }) => {
    if (previewMode && !cardData[field]) return null;
    return (
      <div style={{ borderTop: '1px solid #e5e5e5', paddingTop: 12, marginBottom: 12 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 900, color: '#222', textTransform: 'uppercase', letterSpacing: 1.5 }}>{title}</h3>
        {previewMode ? (
          <div style={{ fontSize: 13, color: '#444', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{cardData[field]}</div>
        ) : (
          <textarea value={cardData[field]} onChange={e => setCardData(p => ({ ...p, [field]: e.target.value }))}
            style={{ ...iStyle, width: '100%', fontSize: 13, color: '#444', lineHeight: 1.7, resize: 'vertical', minHeight: 50 }} rows={3} />
        )}
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,17,17,0.55)', zIndex: 9999999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#FFFDF9', borderRadius: 2, width: 850, maxWidth: '98vw', maxHeight: '96vh', overflow: 'auto', boxShadow: 'none', border: '1px solid rgba(17,17,17,.12)', fontFamily: "'Jost', 'Noto Sans SC', 'PingFang SC', sans-serif" }}>

        {/* Toolbar */}
        <div style={{ padding: '10px 20px', borderBottom: '1px solid #111111', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', position: 'sticky', top: 0, background: '#FFFDF9', zIndex: 10 }}>
          <h3 style={{ margin: 0, fontFamily: "'Cormorant Garamond', 'Noto Serif SC', Georgia, serif", fontSize: 22, fontWeight: 500, color: '#111111' }}>{isZh ? '生成相亲卡片' : 'Generate Matchmaking Card'}</h3>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', borderRadius: 2, overflow: 'hidden', border: '1px solid #111111', marginRight: 4 }}>
              <button onClick={() => setCardLang('en')} style={{ padding: '5px 12px', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 500, letterSpacing: '.14em', background: cardLang==='en'?'#111111':'#FFFDF9', color: cardLang==='en'?'#F6F3EE':'#111111' }}>EN</button>
              <button onClick={() => setCardLang('zh')} style={{ padding: '5px 12px', border: 'none', borderLeft: '1px solid #111111', cursor: 'pointer', fontSize: 11, fontWeight: 500, letterSpacing: '.14em', background: cardLang==='zh'?'#111111':'#FFFDF9', color: cardLang==='zh'?'#F6F3EE':'#111111' }}>中文</button>
            </div>
            <button onClick={() => setPreviewMode(false)} style={{ padding: '6px 12px', borderRadius: 2, border: '1px solid #111111', background: !previewMode?'#111111':'#FFFDF9', color: !previewMode?'#F6F3EE':'#111111', cursor: 'pointer', fontWeight: 500, letterSpacing: '.12em', fontSize: 12 }}>{isZh?'编辑':'Edit'}</button>
            <button onClick={() => setPreviewMode(true)} style={{ padding: '6px 12px', borderRadius: 2, border: '1px solid #111111', background: previewMode?'#111111':'#FFFDF9', color: previewMode?'#F6F3EE':'#111111', cursor: 'pointer', fontWeight: 500, letterSpacing: '.12em', fontSize: 12 }}>{isZh?'预览':'Preview'}</button>
            <button onClick={handleDownload} disabled={downloading} style={{ padding: '6px 16px', borderRadius: 2, border: '1px solid #111111', background: downloading?'#6E685F':'#111111', color: '#F6F3EE', cursor: downloading?'wait':'pointer', fontWeight: 500, letterSpacing: '.14em', fontSize: 12, minWidth: 64 }}>
              {downloading ? `${progress?.pct ?? 0}%` : (isZh ? '下载' : 'Download')}
            </button>
            <button onClick={onClose} style={{ padding: '6px 10px', borderRadius: 2, border: '1px solid rgba(17,17,17,.2)', background: 'transparent', color: '#111111', cursor: 'pointer', fontSize: 14 }}>x</button>
          </div>
        </div>

        {/* A real, clickable download link — Safari honours a click inside a
            user gesture even when it ignored the programmatic one. */}
        {manualUrl && (
          <div style={{ padding: '8px 20px', borderBottom: '1px solid rgba(17,17,17,.08)', background: '#E7EFE9', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#3F7D5A' }}>
              {isZh ? '图片已生成。没有自动下载的话，点这里：' : 'Image ready. If it did not download automatically:'}
            </span>
            <a href={manualUrl} download
              style={{ fontSize: 12, fontWeight: 500, letterSpacing: '.12em', color: '#F6F3EE', background: '#111111', borderRadius: 2, padding: '5px 12px', textDecoration: 'none' }}>
              {isZh ? '下载相亲卡片' : 'Download the card'}
            </a>
          </div>
        )}

        {/* Download progress — generation → upload → download. The stage text
            and percentage are rendered INSIDE the bar so it's always visible. */}
        {progress && (
          <div style={{ padding: '10px 20px 12px', borderBottom: '1px solid rgba(17,17,17,.08)', background: '#FFFDF9', position: 'sticky', top: 49, zIndex: 9 }}>
            <div style={{ position: 'relative', height: 22, background: '#EFEAE2', borderRadius: 0, overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, width: `${progress.pct}%`, background: progress.error ? '#9E2B25' : '#111111', borderRadius: 0, transition: 'width .25s ease' }} />
              {/* Centered text overlay — readable on both the filled (white) and
                  unfilled (dark) parts thanks to the mix-blend difference. */}
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 12, fontWeight: 500, letterSpacing: '.08em', color: '#fff', mixBlendMode: 'difference', whiteSpace: 'nowrap' }}>
                <span>{progress.label}</span>
                <span>{progress.error ? '!' : `${progress.pct}%`}</span>
              </div>
            </div>
          </div>
        )}

        {/* Card — fixed 794px width; height auto-adapts to content (no fixed
            min-height) so long bios / many fields are never clipped. */}
        <div id="matchmaking-card-content" ref={cardRef} style={{ width: 794, minHeight: 480, padding: '24px 36px 28px', background: '#fff', position: 'relative', overflow: 'hidden', boxSizing: 'border-box', margin: '0 auto' }}>
          {/* Watermark — a single large centered logo, OVERLAID on top of the
              content (incl. the member photos) so it acts as an anti-copy mark
              over the photo, not behind it. High zIndex + pointerEvents none. */}
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(-30deg)', pointerEvents: 'none', zIndex: 5, opacity: 0.18 }}>
            <img src="/logo_background.png" alt="" style={{ width: 560, height: 'auto' }} />
          </div>

          <div style={{ display: 'flex', gap: 28, position: 'relative', zIndex: 1 }}>
            {/* LEFT */}
            <div style={{ flex: '0 0 57%', minWidth: 0 }}>
              {/* Name + ID */}
              <div style={{ marginBottom: 4, display: 'flex', alignItems: 'baseline', gap: 10 }}>
                {previewMode ? (
                  <span style={{ fontSize: 26, fontWeight: 700, fontFamily: "'Georgia','Noto Serif SC',serif", color: '#1a1a1a' }}>{cardData.name}</span>
                ) : (
                  <input value={cardData.name} onChange={e => setCardData(p => ({...p, name: e.target.value}))}
                    style={{ ...iStyle, fontSize: 26, fontWeight: 700, fontFamily: "'Georgia','Noto Serif SC',serif", flex: 1, color: '#1a1a1a', minWidth: 0 }} />
                )}
                {cardData.offlineId && <span style={{ fontSize: 12, color: '#999', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>#{cardData.offlineId}</span>}
              </div>

              {(cardData.location || !previewMode) && (
                <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>
                  {previewMode ? cardData.location : (
                    <input value={cardData.location} onChange={e => setCardData(p => ({...p, location: e.target.value}))}
                      style={{ ...iStyle, fontSize: 12, color: '#888', width: '100%' }} />
                  )}
                </div>
              )}

              {(cardData.about || !previewMode) && <TextBlock title={isZh ? '关于我' : 'ABOUT'} field="about" />}

              {/* BASICS — directly below 关于我 / ABOUT */}
              {(hasBasics || !previewMode) && (
                <div style={{ borderTop: '1px solid #e5e5e5', paddingTop: 12, marginBottom: 12 }}>
                  <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 900, color: '#222', textTransform: 'uppercase', letterSpacing: 1.5 }}>{isZh ? '基础信息' : 'BASICS'}</h3>
                  {/* two columns */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 18, rowGap: 0 }}>
                    <Field label={isZh?'年龄':'Age'} field="age" />
                    <Field label={isZh?'身高/体型':'Height/Build'} field="height" />
                    <Field label={isZh?'婚姻状况':'Marital Status'} field="maritalStatus" />
                    <Field label={isZh?'小孩情况':'Child status'} field="childStatus" />
                    <Field label={isZh?'学历':'Education'} field="education" />
                    <Field label={isZh?'职业':'Occupation'} field="occupation" />
                    <Field label={isZh?'族裔':'Race'} field="race" />
                    <Field label={isZh?'月收入':'Monthly Income'} field="income" />
                    <Field label={isZh?'房产':'Real Estate'} field="realEstate" />
                  </div>
                </div>
              )}

              {(cardData.personality || !previewMode) && <TextBlock title={isZh ? '性格特征' : 'PERSONALITY'} field="personality" />}
              {(cardData.interests || !previewMode) && <TextBlock title={isZh ? '兴趣爱好' : 'INTERESTS'} field="interests" />}

              {/* Disclaimer */}
              <div style={{ marginTop: 10, marginBottom: 0, paddingTop: 8, borderTop: '1px solid #e5e5e5', fontSize: 11, color: '#bbb', display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                <WarningIcon size={12} color="#bbb" />
                <span style={{ lineHeight: 1.3 }}>{isZh
                  ? '风险提示：资料均由被推荐人本人提供，本公司不对其真实性作担保。如有需要，可委托合作律师或第三方机构进行背景调查。'
                  : 'Risk notice: all details were provided by the referred individual and are not guaranteed by us. Background checks are available through partner attorneys or third-party agencies on request.'}</span>
              </div>
              <EventMemberRiskNotice
                isEventMember={profile?.is_event_member}
                language={cardLang}
                style={{ marginTop: 6 }}
              />
            </div>

            {/* RIGHT - Photos (max 2) + Basics below them */}
            <div style={{ flex: '0 0 39%', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {photoList.length > 0 ? photoList.map((url, i) => (
                <div key={i} style={{ borderRadius: 8, overflow: 'hidden', background: '#f0f0f0' }}>
                  <img src={url} alt="" style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 8 }}
                    onError={e => { e.target.parentElement.style.display='none'; }} />
                </div>
              )) : (
                <div style={{ borderRadius: 8, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 13, height: 300 }}>
                  {isZh ? '暂无照片' : 'No photos'}
                </div>
              )}
            </div>
          </div>

          {/* Logo — in normal flow at the bottom so it never overlaps content
              no matter how tall the card grows. */}
          <div style={{ position: 'relative', zIndex: 2, marginTop: 20, paddingTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src="/logo_background.png" alt="FINDU" style={{ height: 18, width: 'auto', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: 'rgb(100, 100, 100)' }}>| www.findu.love | support@findu.love</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchmakingCard;
